<script setup>
import { ref, computed, watch } from 'vue'
import SubjectSelectionModal from '@/Components/Modals/SubjectSelectionModal.vue'

const props = defineProps({
  pricingLimits: {
    type: Object,
    required: true
  },
  userSubjects: {
    type: Array,
    default: () => []
  },
  userLevels: {
    type: Array,
    default: () => []
  },
  initialSelectedSubjects: {
    type: Array,
    default: () => []
  },
  initialPricing: {
    type: Object,
    default: () => ({})
  },
  hasBasicMethods: {
    type: Boolean,
    default: false
  },
  hasSilverMethods: {
    type: Boolean,
    default: false
  },
  hasGoldMethods: {
    type: Boolean,
    default: false
  },
  errors: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['update:pricing', 'update:subjects'])

/**
 * What each slider is allowed to offer.
 *
 * These bounds used to be inlined at a dozen call sites as `|| 20`, `|| 80`,
 * `|| 150` — each one a copy of a floor the platform no longer has, and each
 * free to contradict the setting it was standing in for. They are resolved once
 * here, and the fallbacks match what the settings actually hold.
 */
/**
 * Five pence.
 *
 * It was a penny, which made both ends of the range reachable but put roughly
 * ten thousand stops between them: landing on £50 exactly was luck. A whole
 * pound is too coarse for Bindle Live, whose whole range is £1–£10.
 *
 * The step alone is not enough. A slider steps from its **floor**, so with a
 * floor of £0.99 every stop lands at x.99 or x.04 and no whole pound is
 * reachable at all — which is the fault being reported. The floor is therefore
 * snapped up onto the same grid, and the ceiling down onto it, so £1.00, £50.00
 * and £100.00 are all stops.
 */
const PRICE_STEP = 0.05

/** Rounded to the nearest penny: 0.1 + 0.2 is not 0.3 in binary floating point. */
const toGrid = (value, direction) => {
  const steps = direction === 'up'
    ? Math.ceil(value / PRICE_STEP)
    : Math.floor(value / PRICE_STEP)

  return Math.round(steps * PRICE_STEP * 100) / 100
}

const limits = computed(() => ({
  base: {
    min: toGrid(props.pricingLimits?.base?.min ?? 0.99, 'up'),
    average: props.pricingLimits?.base?.average ?? 4,
    max: toGrid(props.pricingLimits?.base?.max ?? 8, 'down'),
  },
  silver: {
    min: toGrid(props.pricingLimits?.silver?.min ?? 0.99, 'up'),
    average: props.pricingLimits?.silver?.average ?? 5,
    max: toGrid(props.pricingLimits?.silver?.max ?? 10, 'down'),
  },
  gold: {
    min: toGrid(props.pricingLimits?.gold?.min ?? 0.99, 'up'),
    average: props.pricingLimits?.gold?.average ?? 50,
    max: toGrid(props.pricingLimits?.gold?.max ?? 100, 'down'),
  },
}))

const formatPrice = (value) => Number(value ?? 0).toFixed(2)

/**
 * A price saved before these bounds changed is brought inside them.
 *
 * A draft holding £15 for Bindle Live, from when the ceiling was £40, left the
 * slider pinned at £10 while the figure beside it still read £15 — and the save
 * was then rejected by the same bounds the slider is drawn from. Clamping on the
 * way in keeps the two in agreement and the submission valid.
 *
 * It is snapped onto the step grid for the same reason: a browser silently
 * moves a range input to the nearest valid stop, so a stored price off the grid
 * would be shown by the slider and by the figure beside it as two different
 * numbers. Every price on record is already a multiple of five pence.
 */
const clampPrice = (value, tier) => {
  const { min, max } = limits.value[tier]
  const inRange = Math.min(Math.max(Number(value), min), max)

  return Math.min(Math.max(toGrid(inRange, 'down'), min), max)
}

const fillPercent = (price, tier) => {
  const { min, max } = limits.value[tier]
  const span = max - min

  return span > 0 ? ((price - min) / span) * 100 : 0
}

// Pricing state - prioritize initial pricing, fall back to minimum
const basicPrice = ref(
  (props.initialPricing?.basic && props.initialPricing.basic > 0)
    ? clampPrice(props.initialPricing.basic, 'base')
    : limits.value.base.min
)
const silverPrice = ref(
  (props.initialPricing?.silver && props.initialPricing.silver > 0)
    ? clampPrice(props.initialPricing.silver, 'silver')
    : limits.value.silver.min
)
const goldPrice = ref(
  (props.initialPricing?.gold && props.initialPricing.gold > 0)
    ? clampPrice(props.initialPricing.gold, 'gold')
    : limits.value.gold.min
)

// Selected subjects for group tutoring
const selectedGroupSubjects = ref([...props.initialSelectedSubjects])
const selectedGroupSubjectsCount = computed(() => selectedGroupSubjects.value.length)

// Watch for changes to initialSelectedSubjects prop
watch(() => props.initialSelectedSubjects, (newSubjects) => {
  selectedGroupSubjects.value = [...newSubjects]
}, { deep: true })

// Watch for changes to initialPricing prop
watch(() => props.initialPricing, (newPricing) => {
  if (newPricing?.basic && newPricing.basic > 0) {
    basicPrice.value = clampPrice(newPricing.basic, 'base')
  } else if (!newPricing?.basic || newPricing.basic === 0) {
    basicPrice.value = limits.value.base.min
  }

  if (newPricing?.silver && newPricing.silver > 0) {
    silverPrice.value = clampPrice(newPricing.silver, 'silver')
  } else if (!newPricing?.silver || newPricing.silver === 0) {
    silverPrice.value = limits.value.silver.min
  }

  if (newPricing?.gold && newPricing.gold > 0) {
    goldPrice.value = clampPrice(newPricing.gold, 'gold')
  } else if (!newPricing?.gold || newPricing.gold === 0) {
    goldPrice.value = limits.value.gold.min
  }
}, { deep: true })

// Modal state
const showSubjectModal = ref(false)

const openSubjectModal = () => {
  showSubjectModal.value = true
}

const handleSubjectsUpdate = (subjects) => {
  selectedGroupSubjects.value = subjects
  emit('update:subjects', subjects)
}

/**
 * The slider warns once a price is above the recommended figure.
 *
 * The two colours were a hardcoded amber and a hardcoded blue, neither of which
 * is in the palette — the blue in particular read as a stray browser default
 * beside the brand teal everywhere else on the page.
 */
const getSliderColor = (price, min, max, recommended) =>
  price > recommended ? 'accent-warning' : 'accent-brand'

const basicSliderColor = computed(() =>
  getSliderColor(basicPrice.value, limits.value.base.min, limits.value.base.max, limits.value.base.average)
)

const silverSliderColor = computed(() =>
  getSliderColor(silverPrice.value, limits.value.silver.min, limits.value.silver.max, limits.value.silver.average)
)

const goldSliderColor = computed(() =>
  getSliderColor(goldPrice.value, limits.value.gold.min, limits.value.gold.max, limits.value.gold.average)
)

// Helper to get gradient color hex value
const getGradientColor = (price, average) =>
  price > average ? 'var(--warning)' : 'var(--brand)'

// Computed gradient colors for each tier
const basicGradientColor = computed(() =>
  getGradientColor(basicPrice.value, limits.value.base.average)
)

const silverGradientColor = computed(() =>
  getGradientColor(silverPrice.value, limits.value.silver.average)
)

const goldGradientColor = computed(() =>
  getGradientColor(goldPrice.value, limits.value.gold.average)
)

/**
 * Emitted immediately as well as on change, so a price clamped into range on
 * the way in reaches the step that submits it instead of leaving the old,
 * out-of-range figure there to be rejected.
 */
watch([basicPrice, silverPrice, goldPrice], () => {
  emit('update:pricing', {
    basic: basicPrice.value,
    silver: silverPrice.value,
    gold: goldPrice.value
  })
}, { immediate: true })
</script>

<template>
  <div class="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2">
    <!--
      The library-only plan is not offered here any more. It is no longer sold on
      its own — both plans include the tutor's material — so asking a tutor to
      price it invited them to set up something no visitor can buy. Their stored
      price is untouched and is still submitted below, so nothing is lost if the
      plan ever returns.
    -->
    <!-- Silver Plan -->
    <section id="silver-plan-pricing">
      <div
        class="flex h-full flex-col rounded-2xl border-2 p-8 transition-all"
        :class="hasSilverMethods
          ? 'border-line-strong bg-surface hover:border-live hover:shadow-lg'
          : 'border-line bg-surface-2 opacity-60'"
      >
        <!--
          Header, badge included, laid out identically on both cards so the two
          sit level. The 1:1 card carried its "POPULAR" flag as an absolutely
          positioned tab in the corner, which hung over the card's own rounded
          edge; it is a chip in this row now, like every other badge on the site.
        -->
        <!--
          The badge sits on its own row, and the row is present on both cards
          whether or not it holds anything. Put beside the title it stole width
          from the subtitle, wrapped it onto a second line on one card only, and
          pushed everything below it out of step with its twin.
        -->
        <div class="mb-3 flex h-6 items-start justify-end"></div>

        <div class="mb-6 flex items-center gap-4">
          <div class="grid size-16 shrink-0 place-items-center rounded-2xl bg-live-soft">
            <span class="material-symbols-outlined text-3xl text-live-soft-text">sensors</span>
          </div>
          <div class="min-w-0">
            <h3 class="text-xl font-bold text-ink">Bindle Live</h3>
            <p class="text-sm text-muted">A weekly live class + your content library</p>
          </div>
        </div>
        <div class="space-y-3 mb-8">
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-success">check</span>
            <span class="text-sm font-semibold text-ink">A weekly live class following a course</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-success">check</span>
            <span class="text-sm text-muted">Your videos and study material</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-success">check</span>
            <span class="text-sm text-muted">Every class recorded for the group</span>
          </div>
        </div>

        <!-- Spacer to push content to bottom -->
        <div class="flex-grow"></div>

        <!-- Not Offered Message -->
        <div v-if="!hasSilverMethods" class="mt-6 rounded-xl border border-warning bg-warning-bg p-4">
          <div class="flex items-center gap-2 text-warning">
            <span class="material-symbols-outlined text-lg">info</span>
            <p class="text-sm font-medium">You do not offer this service</p>
          </div>
          <p class="mt-1 text-xs text-warning">Go back to Services Offered and select group tutoring methods to enable Bindle Live</p>
        </div>

        <!-- Group Tutoring Subjects Button -->
        <div v-else-if="userSubjects.length > 0" class="mb-6">
          <div class="flex items-center justify-between mb-3">
            <span class="text-sm font-semibold text-ink">Bindle Live subjects</span>
            <span class="text-xs font-medium text-live-soft-text">{{ selectedGroupSubjectsCount }} selected</span>
          </div>

          <button
            type="button"
            @click="openSubjectModal"
            class="group flex w-full items-center justify-between rounded-xl border-2 border-line-strong p-4 transition-all hover:border-live hover:bg-live-soft"
          >
            <span class="text-sm font-medium text-ink">Select subjects to offer</span>
            <span class="material-symbols-outlined text-live-soft-text transition-transform group-hover:translate-x-1">arrow_forward</span>
          </button>
        </div>

        <!-- Pricing Control -->
        <div v-if="hasSilverMethods" class="pricing-control mt-6">
          <div class="flex justify-between items-center mb-4">
            <span class="text-sm font-semibold text-ink">Weekly Price</span>
            <span class="rounded-full bg-surface-2 px-3 py-1 text-xs text-muted">
              Recommended: £{{ formatPrice(limits.silver.average) }}
            </span>
          </div>
          <div class="relative mb-6">
            <input
              v-model.number="silverPrice"
              type="range"
              :min="limits.silver.min"
              :max="limits.silver.max"
              :step="PRICE_STEP"
              class="w-full h-3 rounded-lg appearance-none cursor-pointer slider"
              :class="silverSliderColor"
              :style="{
                background: `linear-gradient(to right,
                  ${silverGradientColor} 0%,
                  ${silverGradientColor} ${fillPercent(silverPrice, 'silver')}%,
                  var(--line-strong) ${fillPercent(silverPrice, 'silver')}%,
                  var(--line-strong) 100%)`
              }"
            >
            <div class="mt-2 flex justify-between text-xs text-muted">
              <span>£{{ formatPrice(limits.silver.min) }}</span>
              <span>£{{ formatPrice(limits.silver.max) }}</span>
            </div>
          </div>
          <div class="rounded-xl bg-live-soft py-6 text-center">
            <span class="text-4xl font-bold text-ink">£{{ formatPrice(silverPrice) }}</span>
            <span class="text-lg font-bold text-muted"> weekly subscription</span>
          </div>
          <!-- Error message for silver pricing -->
          <div class="mt-3 min-h-[3rem]">
            <div v-if="errors['pricing.silver']" class="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div class="flex items-start gap-2">
                <span class="material-symbols-outlined text-red-600 text-sm">error</span>
                <p class="text-sm text-red-700">{{ errors['pricing.silver'] }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Gold Plan -->
    <section id="gold-plan-pricing">
      <div
        class="flex h-full flex-col rounded-2xl border-2 p-8 transition-all"
        :class="hasGoldMethods
          ? 'border-line-brand bg-surface hover:border-brand hover:shadow-lg'
          : 'border-line bg-surface-2 opacity-60'"
      >
        <div class="mb-3 flex h-6 items-start justify-end">
          <span
            v-if="hasGoldMethods"
            class="rounded-full bg-brand-soft px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-brand-soft-text"
          >
            Most popular
          </span>
        </div>

        <div class="mb-6 flex items-center gap-4">
          <div class="grid size-16 shrink-0 place-items-center rounded-2xl bg-brand-soft">
            <span class="material-symbols-outlined text-3xl text-brand">workspace_premium</span>
          </div>
          <div class="min-w-0">
            <h3 class="text-xl font-bold text-ink">1:1 Tutoring</h3>
            <p class="text-sm text-muted">A weekly one-to-one lesson + your content library</p>
          </div>
        </div>
        <div class="space-y-3 mb-8">
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-success">check</span>
            <span class="text-sm text-muted">Content Library access</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-success">check</span>
            <span class="text-sm font-semibold text-ink">Weekly 1:1 sessions</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-success">check</span>
            <span class="text-sm text-muted">Direct messaging with tutor</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-success">check</span>
            <span class="text-sm text-muted">Personalized study plans</span>
          </div>
          <div class="flex items-center space-x-3">
            <span class="material-symbols-outlined text-success">check</span>
            <span class="text-sm text-muted">Priority support</span>
          </div>
        </div>

        <!-- Spacer to push pricing to bottom -->
        <div class="flex-grow"></div>

        <!-- Not Offered Message -->
        <div v-if="!hasGoldMethods" class="mt-6 rounded-xl border border-warning bg-warning-bg p-4">
          <div class="flex items-center gap-2 text-warning">
            <span class="material-symbols-outlined text-lg">info</span>
            <p class="text-sm font-medium">You do not offer this service</p>
          </div>
          <p class="mt-1 text-xs text-warning">Go back to Services Offered and select 1:1 tutoring methods to enable this tier</p>
        </div>

        <!-- Pricing Control -->
        <div v-else class="pricing-control mt-6">
          <div class="flex justify-between items-center mb-4">
            <span class="text-sm font-semibold text-ink">Weekly Price</span>
            <span class="rounded-full bg-surface-2 px-3 py-1 text-xs text-muted">
              Recommended: £{{ formatPrice(limits.gold.average) }}
            </span>
          </div>
          <div class="relative mb-6">
            <input
              v-model.number="goldPrice"
              type="range"
              :min="limits.gold.min"
              :max="limits.gold.max"
              :step="PRICE_STEP"
              class="w-full h-3 rounded-lg appearance-none cursor-pointer slider"
              :class="goldSliderColor"
              :style="{
                background: `linear-gradient(to right,
                  ${goldGradientColor} 0%,
                  ${goldGradientColor} ${fillPercent(goldPrice, 'gold')}%,
                  var(--line-strong) ${fillPercent(goldPrice, 'gold')}%,
                  var(--line-strong) 100%)`
              }"
            >
            <div class="mt-2 flex justify-between text-xs text-muted">
              <span>£{{ formatPrice(limits.gold.min) }}</span>
              <span>£{{ formatPrice(limits.gold.max) }}</span>
            </div>
          </div>
          <div class="rounded-xl bg-brand-soft py-6 text-center">
            <span class="text-4xl font-bold text-ink">£{{ formatPrice(goldPrice) }}</span>
            <span class="text-lg font-bold text-muted"> weekly subscription</span>
          </div>
          <!-- Error message for gold pricing -->
          <div class="mt-3 min-h-[3rem]">
            <div v-if="errors['pricing.gold']" class="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div class="flex items-start gap-2">
                <span class="material-symbols-outlined text-red-600 text-sm">error</span>
                <p class="text-sm text-red-700">{{ errors['pricing.gold'] }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>

  <!-- Subject Selection Modal -->
  <SubjectSelectionModal
    :show="showSubjectModal"
    :subjects="userSubjects"
    :levels="userLevels"
    :selected-subjects="selectedGroupSubjects"
    @close="showSubjectModal = false"
    @update:selected="handleSubjectsUpdate"
  />
</template>

<style scoped>
/* Custom slider styling */
input[type="range"].slider {
  -webkit-appearance: none;
  appearance: none;
  height: 12px;
  border-radius: 6px;
  outline: none;
}

input[type="range"].slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  border: 3px solid currentColor;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}

input[type="range"].slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}

input[type="range"].slider::-moz-range-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  border: 3px solid currentColor;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}

input[type="range"].slider::-moz-range-thumb:hover {
  transform: scale(1.1);
}

input[type="range"].slider::-moz-range-track {
  height: 12px;
  border-radius: 6px;
}

.accent-brand {
  color: var(--brand);
}

.accent-warning {
  color: var(--warning);
}
</style>
